import React, { useEffect, useState } from 'react';
import { Field, FieldArray, useFormikContext } from 'formik';
import { Delete, Add, Palette } from '@mui/icons-material';
import { Popover } from '@mui/material';
import { Typography } from '@material-tailwind/react';

import {
  BiFieldTypeList,
  BiReportType,
  BiFilterFieldTypeList,
  BiAnalysisTypeList,
  BiFieldType,
  BiChartTypeList,
} from '../../../config/bi_report';
import {
  InputUI,
  FormErrorUI,
  FormLabelUI,
  AutoCompleteSelectUI,
  ButtonUI,
  SelectUI,
  CheckboxUI,
  ChromeColorPickerUI,
} from '../../Interface';
import biService from '../../../service/bi.service';
import BiConditionDialog from './BiConditionDialog';
import BiFieldColorDialog from './BiFieldColorDialog';

const COLORABLE_REPORT_TYPES = [
  BiReportType.BAR,
  BiReportType.HORIZONTAL,
  BiReportType.LINE,
  BiReportType.COMBO,
  BiReportType.STACKED_BAR,
];

const DATA_LABEL_REPORT_TYPES = [
  BiReportType.PIE,
  BiReportType.DOUGHNUT,
  ...COLORABLE_REPORT_TYPES,
];

const SINGLE_SERIES_REPORT_TYPES = [BiReportType.PIE, BiReportType.DOUGHNUT, BiReportType.KPI];

const BiFieldColorPicker = ({ color, onChange, onRemove }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <button
        type="button"
        aria-label="Diagramszín kiválasztása"
        className="rounded-full border border-gray-300 shadow-[rgba(0,_0,_0,_0.54)_0px_1px_8px] w-[20px] h-[20px] mt-1"
        style={{ background: color ?? '#ffffff' }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <ChromeColorPickerUI
          color={color}
          onChange={onChange}
          removeColor={() => {
            onRemove();
            setAnchorEl(null);
          }}
        />
      </Popover>
    </>
  );
};

const BiFieldForm = ({ defaultField, autocompleteData, fieldErrorState }) => {
  const { fieldErrors } = fieldErrorState;
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const [reportFields, setReportFields] = useState([]);
  const [groupFields, setGroupFields] = useState([]);
  const [filterFields, setFilterFields] = useState([]);
  const [relevantReports, setRelevantReports] = useState([]);
  const [conditionalFieldIndex, setConditionalFieldIndex] = useState(null);
  const [colorFieldIndex, setColorFieldIndex] = useState(null);

  useEffect(() => {
    if (values.biField) {
      const { grpFields, rptFields, filterFields } = values.biField.reduce(
        (acc, item, index) => {
          const itemWithIndex = { index, ...item };
          if (item.isGroup && values.reportType !== BiReportType.TABLE) {
            acc.grpFields.push(itemWithIndex);
          } else if (item.isFilter) {
            acc.filterFields.push(itemWithIndex);
          } else {
            acc.rptFields.push(itemWithIndex);
          }
          return acc;
        },
        { grpFields: [], rptFields: [], filterFields: [] }
      );
      setReportFields(rptFields);
      setGroupFields(grpFields);
      setFilterFields(filterFields);
    }
  }, [values.biField]);

  const getAllBiReports = () => {
    biService.allBiReports().then((data) => {
      const reports = data
        .filter((report) => {
          if (report.reportType === BiReportType.TABLE && report.biHeadId === values.biHeadId) {
            return report.reportType === BiReportType.TABLE;
          }
        })
        .map((report) => ({ value: report.id, label: report.name }));
      setRelevantReports(reports);
    });
  };

  useEffect(() => {
    if (!values.reportType || values.reportType === BiReportType.TABLE || !values.biHeadId) {
      setRelevantReports([]);
      return;
    }

    getAllBiReports();
  }, [values.biHeadId, values.reportType]);

  const getBiFieldLabel = (fieldValue) => {
    return autocompleteData.biField?.find((item) => item.value === fieldValue)?.label ?? '';
  };

  return (
    <FieldArray name="biField">
      {({ remove, push }) => (
        <>
          <div className="grid grid-cols-3 gap-x-4">
            <div className="col-span-1 flex items-center mx-4">
              <Typography variant="h6" className="mr-2">
                Mezők
              </Typography>
              {!SINGLE_SERIES_REPORT_TYPES.includes(values.reportType) && (
                <ButtonUI
                  text={<Add fontSize="small" />}
                  className="bg-success p-0.5"
                  type="button"
                  onClick={() => {
                    push(defaultField);
                  }}
                  disabled={!(values.biHeadId && values.reportType)}
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-4 mx-2 mb-2 justify-items-center items-center">
            {fieldErrors.length > 0 &&
              fieldErrors.map((error) => (
                <div className="col-span-1" key={error.id}>
                  <FormErrorUI message={error.message} />
                </div>
              ))}
          </div>
          <div>
            {reportFields.length > 0 &&
              reportFields.map((biField, idx) => (
                <div
                  className={`grid grid-cols-12 gap-x-4 px-4 pb-2 pt-2 ${idx % 2 === 0 ? 'bg-gray-100' : ''}`}
                  key={biField.index}
                >
                  <div className="col-span-3 ">
                    {biField.type === BiFieldType.CALCULATED ? (
                      <Field
                        type="text"
                        name={`biField.${biField.index}.fnctn`}
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Képlet" />}
                        placeholder="Pl.: ([ELADOTT] / [ÖSSZES]) * 100"
                        variant="standard"
                        helperText={<FormErrorUI message={errors.biField?.[biField.index]?.fnctn} />}
                        InputLabelProps={{ shrink: !!biField.fnctn }}
                      />
                    ) : biField.type === BiFieldType.CONDITIONAL ? (
                      <div className="flex flex-col gap-1">
                        <ButtonUI
                          type="button"
                          text={`Feltételek (${biField.biFieldCondition?.length ?? 0})`}
                          className="bg-primary"
                          onClick={() => setConditionalFieldIndex(biField.index)}
                        />
                        <FormErrorUI message={errors.biField?.[biField.index]?.biFieldCondition} />
                      </div>
                    ) : (
                      <Field
                        type="autocomplete"
                        name={`biField.${biField.index}.field`}
                        component={AutoCompleteSelectUI}
                        fullWidth
                        label={<FormLabelUI text="Mező" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.biField?.[biField.index]?.field} />}
                        inputLabelProps={{ shrink: biField.field !== null }}
                        onChange={(_e, newVal) => {
                          setFieldValue(`biField.${biField.index}.field`, newVal?.value ?? null).then(() => {
                            validateField(`biField.${biField.index}.field`);
                          });
                        }}
                        selectedValue={biField.field}
                        selectedLabelValue={getBiFieldLabel(biField.field)}
                        dataset={autocompleteData.biField}
                      />
                    )}
                  </div>
                  {values.reportType === BiReportType.TABLE && (
                    <>
                      <div className="col-span-2 ">
                        <Field
                          name={`biField.${biField.index}.type`}
                          component={SelectUI}
                          fullWidth
                          option={BiFieldTypeList}
                          value={biField.type ?? ''}
                          variant="standard"
                          onChange={(e) => {
                            const newType = e.target.value ?? null;
                            if (newType === BiFieldType.CALCULATED) {
                              setFieldValue(`biField.${biField.index}.field`, null);
                              setFieldValue(`biField.${biField.index}.isGroup`, false);
                              setFieldValue(`biField.${biField.index}.biFieldCondition`, []);
                            } else if (newType === BiFieldType.CONDITIONAL) {
                              setFieldValue(`biField.${biField.index}.field`, null);
                              setFieldValue(`biField.${biField.index}.fnctn`, '');
                              setFieldValue(`biField.${biField.index}.isGroup`, false);
                              setFieldValue(
                                `biField.${biField.index}.biFieldCondition`,
                                values.biField?.[biField.index]?.biFieldCondition ?? []
                              );
                              setConditionalFieldIndex(biField.index);
                            } else {
                              setFieldValue(`biField.${biField.index}.fnctn`, '');
                              setFieldValue(`biField.${biField.index}.biFieldCondition`, []);
                            }
                            setFieldValue(`biField.${biField.index}.type`, newType).then(() => {
                              validateField(`biField.${biField.index}.type`);
                            });
                          }}
                          label={<FormLabelUI text="Mező típus" />}
                          helperText={<FormErrorUI message={errors.biField?.[biField.index]?.type} />}
                          InputLabelProps={{ shrink: values.biField?.[biField.index]?.type !== '' }}
                        />
                      </div>
                    </>
                  )}
                  <div className="col-span-2 ">
                    <Field
                      type="text"
                      name={`biField.${biField.index}.name`}
                      as={InputUI}
                      fullWidth
                      label={<FormLabelUI text="Mező név" />}
                      variant="standard"
                      helperText={<FormErrorUI message={errors.biField?.[biField.index]?.name} />}
                      InputLabelProps={{ shrink: values.biField?.[biField.index]?.name !== '' }}
                    />
                  </div>
                  {values.reportType !== BiReportType.TABLE && (
                    <>
                      <div className="col-span-3 ">
                        <Field
                          name={`biField.${biField.index}.analysis`}
                          component={SelectUI}
                          fullWidth
                          option={BiAnalysisTypeList}
                          value={values.biField?.[biField.index]?.analysis ?? ''}
                          variant="standard"
                          onChange={(e) => {
                            setFieldValue(`biField.${biField.index}.analysis`, e.target.value ?? null).then(() => {
                              validateField(`biField.${biField.index}.analysis`);
                            });
                          }}
                          label={<FormLabelUI text="Analízis" />}
                          helperText={<FormErrorUI message={errors.biField?.[biField.index]?.analysis} />}
                          InputLabelProps={{ shrink: values.biField?.[biField.index]?.analysis }}
                        />
                      </div>
                    </>
                  )}
                  {values.reportType === BiReportType.COMBO && (
                    <div className="col-span-2 ">
                      <Field
                        name={`biField.${biField.index}.chartType`}
                        component={SelectUI}
                        fullWidth
                        option={BiChartTypeList}
                        value={values.biField?.[biField.index]?.chartType ?? ''}
                        variant="standard"
                        onChange={(e) => {
                          setFieldValue(`biField.${biField.index}.chartType`, e.target.value ?? null).then(() => {
                            validateField(`biField.${biField.index}.chartType`);
                          });
                        }}
                        label={<FormLabelUI text="Diagram típus" />}
                        helperText={<FormErrorUI message={errors.biField?.[biField.index]?.chartType} />}
                        InputLabelProps={{ shrink: values.biField?.[biField.index]?.chartType }}
                      />
                    </div>
                  )}
                  {COLORABLE_REPORT_TYPES.includes(values.reportType) && (
                    <div className="col-span-1 flex flex-col items-center">
                      <FormLabelUI text="Szín" />
                      <BiFieldColorPicker
                        color={values.biField?.[biField.index]?.color}
                        onChange={(color) => {
                          setFieldValue(`biField.${biField.index}.color`, color.hex).then(() => {
                            validateField(`biField.${biField.index}.color`);
                          });
                        }}
                        onRemove={() => {
                          setFieldValue(`biField.${biField.index}.color`, null).then(() => {
                            validateField(`biField.${biField.index}.color`);
                          });
                        }}
                      />
                      <FormErrorUI message={errors.biField?.[biField.index]?.color} />
                    </div>
                  )}
                  {DATA_LABEL_REPORT_TYPES.includes(values.reportType) && (
                    <div className="col-span-1 ">
                      <Field
                        name={`biField.${biField.index}.showDataLabels`}
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Érték kiírása" />}
                        checked={Boolean(values.biField?.[biField.index]?.showDataLabels)}
                        onChange={(e) => {
                          setFieldValue(
                            `biField.${biField.index}.showDataLabels`,
                            e.target.checked
                          ).then(() => {
                            validateField(`biField.${biField.index}.showDataLabels`);
                          });
                        }}
                      />
                    </div>
                  )}
                  {(biField.type === BiFieldType.NUMBER ||
                    biField.type === BiFieldType.CURRENCY ||
                    biField.type === BiFieldType.QUANTITY ||
                    biField.type === BiFieldType.PRICE ||
                    biField.type === BiFieldType.SUM ||
                    biField.type === BiFieldType.CALCULATED ||
                    values.reportType === BiReportType.KPI) && (
                    <>
                      <div className="col-span-1 ">
                        <Field
                          type="number"
                          name={`biField.${biField.index}.thresholdMin`}
                          as={InputUI}
                          fullWidth
                          label={<FormLabelUI text="Alsó határérték" />}
                          variant="standard"
                          helperText={<FormErrorUI message={errors.biField?.[biField.index]?.thresholdMin} />}
                          InputLabelProps={{
                            shrink:
                              values.biField?.[biField.index]?.thresholdMin !== '' &&
                              values.biField?.[biField.index]?.thresholdMin !== null,
                          }}
                        />
                      </div>
                      <div className="col-span-1 ">
                        <Field
                          type="number"
                          name={`biField.${biField.index}.thresholdMax`}
                          as={InputUI}
                          fullWidth
                          label={<FormLabelUI text="Felső határérték" />}
                          variant="standard"
                          helperText={<FormErrorUI message={errors.biField?.[biField.index]?.thresholdMax} />}
                          InputLabelProps={{
                            shrink:
                              values.biField?.[biField.index]?.thresholdMax !== '' &&
                              values.biField?.[biField.index]?.thresholdMax !== null,
                          }}
                        />
                      </div>
                      <div className="col-span-1 ">
                        <Field
                          name={`biField.${biField.index}.thresholdArrow`}
                          type="checkbox"
                          as={CheckboxUI}
                          fullWidth
                          label={<FormLabelUI text="Nyíl" />}
                          checked={values.biField?.[biField.index]?.thresholdArrow}
                          onChange={async (e) => {
                            setFieldValue(`biField.${biField.index}.thresholdArrow`, e.target.checked).then(() => {
                              validateField(`biField.${biField.index}.thresholdArrow`);
                            });
                          }}
                        />
                      </div>
                    </>
                  )}
                  {values.reportType === BiReportType.TABLE &&
                    biField.type !== BiFieldType.CALCULATED &&
                    biField.type !== BiFieldType.CONDITIONAL && (
                    <div className="col-span-1 ">
                      <Field
                        name={`biField.${biField.index}.isGroup`}
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Fúrás" />}
                        checked={values.biField?.[biField.index]?.isGroup}
                        onChange={async (e) => {
                          const findCheckedGroup = reportFields.find((fieldItem, idx) => {
                            return fieldItem.isGroup && idx !== biField.index;
                          });
                          if (findCheckedGroup && e.target.checked) {
                            //only one group field allowed
                            setFieldValue(`biField.${findCheckedGroup.index}.isGroup`, false).then(() => {
                              validateField(`biField.${findCheckedGroup.index}.isGroup`);
                            });
                          }

                          setFieldValue(`biField.${biField.index}.isGroup`, e.target.checked).then(() => {
                            validateField(`biField.${biField.index}.isGroup`);
                          });
                        }}
                      />
                    </div>
                  )}
                  {!SINGLE_SERIES_REPORT_TYPES.includes(values.reportType) && (
                    <div className="col-span-1 flex justify-center gap-1">
                      {values.reportType === BiReportType.TABLE && (
                        <button
                          type="button"
                          title={`Cellaszínek (${biField.biFieldColor?.length ?? 0})`}
                          onClick={() => setColorFieldIndex(biField.index)}
                          className="text-primary flex items-center"
                        >
                          <Palette />
                          <span className="text-xs">{biField.biFieldColor?.length ?? 0}</span>
                        </button>
                      )}
                      <button type="button" onClick={() => remove(biField.index)} className="text-red-600 items-center">
                        <Delete />
                      </button>
                    </div>
                  )}
                </div>
              ))}
          </div>

          {groupFields.length > 0 && (
            <>
              <Typography variant="h6" className="grid grid-cols-1 gap-x-4 mx-4 ">
                Csoportosítás
              </Typography>
              {groupFields.map((biField, idx) => (
                <div
                  className={`grid grid-cols-10 gap-x-4 px-4 pb-2 pt-2 md:max-w-[75%] xl:max-w-[65%] ${
                    idx % 2 === 0 ? 'bg-gray-100' : ''
                  }`}
                  key={biField.index}
                >
                  <div className="col-span-3 ">
                    <Field
                      type="autocomplete"
                      name={`biField.${biField.index}.field`}
                      component={AutoCompleteSelectUI}
                      fullWidth
                      label={<FormLabelUI text="Mező" />}
                      variant="standard"
                      helperText={<FormErrorUI message={errors.biField?.[biField.index]?.field} />}
                      inputLabelProps={{ shrink: biField.field !== null }}
                      onChange={(_e, newVal) => {
                        setFieldValue(`biField.${biField.index}.field`, newVal?.value ?? null).then(() => {
                          validateField(`biField.${biField.index}.field`);
                        });
                      }}
                      selectedValue={biField.field}
                      selectedLabelValue={getBiFieldLabel(biField.field)}
                      dataset={autocompleteData.biField}
                    />
                  </div>

                  <div className="col-span-3 ">
                    <Field
                      type="text"
                      name={`biField.${biField.index}.name`}
                      as={InputUI}
                      fullWidth
                      label={<FormLabelUI text="Csoport név" />}
                      variant="standard"
                      helperText={<FormErrorUI message={errors.biField?.[biField.index]?.name} />}
                      InputLabelProps={{ shrink: values.biField?.[biField.index]?.name !== '' }}
                    />
                  </div>
                  <div className="col-span-3 ">
                    <Field
                      type="number"
                      name={`biField.${biField.index}.groupLabelMaxLength`}
                      as={InputUI}
                      fullWidth
                      label={<FormLabelUI text="Csoportfelirat hossza" />}
                      variant="standard"
                      helperText={
                        <FormErrorUI message={errors.biField?.[biField.index]?.groupLabelMaxLength} />
                      }
                      InputLabelProps={{
                        shrink:
                          values.biField?.[biField.index]?.groupLabelMaxLength !== '' &&
                          values.biField?.[biField.index]?.groupLabelMaxLength !== null,
                      }}
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </div>
                </div>
              ))}
            </>
          )}
          {values.reportType !== BiReportType.TABLE && (
            <>
              <div className="col-span-1 flex items-center mx-4">
                <Typography variant="h6" className="mr-2">
                  Szűrő
                </Typography>

                <ButtonUI
                  text={<Add fontSize="small" />}
                  className="bg-success p-0.5"
                  type="button"
                  onClick={() => {
                    const newFilterField = { ...defaultField, isFilter: true };
                    push(newFilterField);
                  }}
                  disabled={!(values.biHeadId && values.reportType)}
                />
              </div>
              {filterFields.length > 0 ? (
                <>
                  {filterFields.map((biField, idx) => (
                    <div
                      className={`grid grid-cols-10 gap-x-4 mx-4 mb-5 md:max-w-[75%] xl:max-w-[65%] ${
                        idx % 2 === 0 ? 'bg-gray-100' : ''
                      }`}
                      key={biField.index}
                    >
                      <div className="col-span-3 ">
                        <Field
                          type="autocomplete"
                          name={`biField.${biField.index}.field`}
                          component={AutoCompleteSelectUI}
                          fullWidth
                          label={<FormLabelUI text="Mező" />}
                          variant="standard"
                          helperText={<FormErrorUI message={errors.biField?.[biField.index]?.field} />}
                          inputLabelProps={{ shrink: biField.field !== null }}
                          onChange={(_e, newVal) => {
                            setFieldValue(`biField.${biField.index}.field`, newVal?.value ?? null).then(() => {
                              validateField(`biField.${biField.index}.field`);
                            });
                          }}
                          selectedValue={biField.field}
                          selectedLabelValue={getBiFieldLabel(biField.field)}
                          dataset={autocompleteData.biField}
                        />
                      </div>
                      <div className="col-span-1 ">
                        <Field
                          name={`biField.${biField.index}.filterType`}
                          component={SelectUI}
                          fullWidth
                          option={BiFilterFieldTypeList}
                          value={biField.filterType ?? ''}
                          variant="standard"
                          onChange={(e) => {
                            setFieldValue(`biField.${biField.index}.filterType`, e.target.value ?? null).then(() => {
                              validateField(`biField.${biField.index}.filterType`);
                            });
                          }}
                          label={<FormLabelUI text="Szűrő típus" />}
                          helperText={<FormErrorUI message={errors.biField?.[biField.index]?.filterType} />}
                          InputLabelProps={{ shrink: values.biField?.[biField.index]?.filterType !== '' }}
                        />
                      </div>
                      <div className="col-span-3 ">
                        <Field
                          multiple
                          freeSolo
                          options={[]}
                          type="autocomplete"
                          name={`biField.${biField.index}.filterFieldValue`}
                          component={AutoCompleteSelectUI}
                          fullWidth
                          label={<FormLabelUI text="Szűrő érték" />}
                          variant="standard"
                          helperText={<FormErrorUI message={errors.biField?.[biField.index]?.filterFieldValue} />}
                          inputLabelProps={{ shrink: biField.field !== null }}
                          onChange={(_e, newVal) => {
                            setFieldValue(`biField.${biField.index}.filterFieldValue`, newVal).then(() => {
                              validateField(`biField.${biField.index}.filterFieldValue`);
                            });
                          }}
                          value={biField.filterFieldValue}
                        />
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <button
                          type="button"
                          onClick={() => remove(biField.index)}
                          className="text-red-600 items-center"
                        >
                          <Delete />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="mx-4 mb-5 md:max-w-[75%] xl:max-w-[65%] min-h-[51px]"></div>
              )}
              <div className="col-span-1 flex items-center mx-4">
                <Typography variant="h6" className="mr-2">
                  Adattábla
                </Typography>
              </div>
              <div className="grid grid-cols-10 gap-x-4 mx-4 mb-5 md:max-w-[75%] xl:max-w-[65%]">
                <div className="col-span-6 ">
                  <Field
                    type="autocomplete"
                    name="tableReportId"
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Táblázatos riport" />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.tableReportId} />}
                    inputLabelProps={{ shrink: values.tableReportId !== '' }}
                    onChange={(_e, newVal) => {
                      setFieldValue(`tableReportId`, newVal?.value ?? null).then(() => {
                        validateField(`tableReportId`);
                      });
                    }}
                    //@TODO ezt ki kell vizsgálni!!!
                    //value={values.tableReportId}
                    selectedValue={values.tableReportId}
                    selectedValueLabel={
                      values.tableReportId
                        ? relevantReports.find((report) => report.value === values.tableReportId)?.label
                        : ''
                    }
                    dataset={relevantReports}
                    isOptionEqualToValue={(option, value) => option.value === value}
                  />
                </div>
              </div>
            </>
          )}
          <BiConditionDialog
            open={conditionalFieldIndex !== null}
            onClose={() => setConditionalFieldIndex(null)}
            biField={
              conditionalFieldIndex !== null ? values.biField?.[conditionalFieldIndex] : null
            }
            availableFields={autocompleteData.biField ?? []}
            onSave={(conditionalValues) => {
              if (conditionalFieldIndex === null) return;

              setFieldValue(`biField.${conditionalFieldIndex}`, {
                ...values.biField[conditionalFieldIndex],
                ...conditionalValues,
              });
              setConditionalFieldIndex(null);
            }}
          />
          <BiFieldColorDialog
            open={colorFieldIndex !== null}
            onClose={() => setColorFieldIndex(null)}
            biField={colorFieldIndex !== null ? values.biField?.[colorFieldIndex] : null}
            onSave={(biFieldColor) => {
              if (colorFieldIndex === null) return;

              setFieldValue(`biField.${colorFieldIndex}.biFieldColor`, biFieldColor);
              setColorFieldIndex(null);
            }}
          />
        </>
      )}
    </FieldArray>
  );
};

export default BiFieldForm;
